import type { TOfficeNotificationKey } from '~/domain/OfficeNotification';
import { ConfirmCandidateSource, type TConfirmCandidateSource } from './types';

/**
 * Reading the confirm source off an office-notification email CTA (GOTU-4135):
 * `utm_source=email` + `utm_campaign=<notification key>`, landing on
 * `/dashboard/job/:jobId`.
 *
 * The other link family that leads into the confirm flow — the confirmation-outreach
 * Branch link, which lands straight on the confirm route — is read on its own terms in
 * `./fromConfirmOutreach`. It is minted by a different sender, carries different
 * params, and is no longer email-only.
 */

/**
 * Maps an office-notification email key (the `utm_campaign` value read by
 * `parseNotificationParams`) to the confirm source it represents.
 *
 * Only the two candidate-applied campaigns lead into the confirm flow;
 * `offer_expiration_warning` lands on the Action Needed list and
 * `end_of_day_review` on a completed shift, so both return `undefined` rather
 * than being forced into a source value. An office who then reaches the confirm
 * screen from the Action Needed list gets `action_needed` from that click, which
 * is what actually drove the confirmation.
 */
export function confirmSourceFromEmailNotification(
  notification?: TOfficeNotificationKey,
): TConfirmCandidateSource | undefined {
  switch (notification) {
    case 'candidate_applied':
      return ConfirmCandidateSource.EmailCandidateApplied;
    case 'candidate_applied_counter':
      return ConfirmCandidateSource.EmailCandidateAppliedCounter;
    default:
      return undefined;
  }
}
